// Redirects to the user's base route once, at the moment of login.
// Login is detected as the transition of `user` from falsy to truthy, so the
// redirect fires exactly once per sign-in and is naturally reset by logout.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFirebase } from './FirebaseContextProvider';
import { getBaseRoute } from './LoginCard';
import { storageTournament } from '../utils/consts';
import { clearSavedTournament } from '../utils/misc';

// Routes a freshly logged-in user should be sent away from. A deep-link refresh
// (e.g. /tournaments/:id) is left alone — only these public entries redirect.
const publicEntryPaths = ['/', '/login'];

export default function LoginRedirect() {
  const {
    user,
    isTournamentsLoaded,
    isSelfSynced,
    tournamentsById,
    userIsAdmin,
    activeOrg,
  } = useFirebase();
  const navigate = useNavigate();
  const location = useLocation();
  const wasAuthed = useRef(!!user);
  const [pending, setPending] = useState(false);
  // Which role the redirect below acted on, so a stale cached one can be undone
  const redirectedForRole = useRef(null);
  // The organization the effect below last saw, so a real switch is told apart
  // from the first resolve
  const lastOrg = useRef(null);

  // Detect the login transition (logged out → logged in). Decoupled from the
  // redirect itself so it survives the gap before tournaments finish loading.
  useEffect(() => {
    if (user && !wasAuthed.current) setPending(true);
    if (!user) setPending(false);
    wasAuthed.current = !!user;
  }, [user]);

  // Once data is ready, redirect — but only from a public entry route.
  useEffect(() => {
    if (!pending || !user) return;

    // Only the admin resume-saved-tournament branch needs the tournaments list;
    // everyone else can be redirected as soon as their role is known
    const savedTournament = localStorage.getItem(storageTournament);
    if (userIsAdmin && savedTournament && !isTournamentsLoaded) return;

    if (!publicEntryPaths.includes(location.pathname)) {
      setPending(false);
      return;
    }
    setPending(false);
    redirectedForRole.current = user.role;

    // Only admins resume the saved tournament schedule; everyone else lands
    // on their base route, where the Live tab (when relevant) is the default
    const savedValid =
      savedTournament && user.role && tournamentsById.has(savedTournament);
    if (userIsAdmin && savedValid) {
      navigate(`/tournaments/${savedTournament}#schedule`, { replace: true });
    } else {
      if (savedTournament && !savedValid) clearSavedTournament();
      navigate(getBaseRoute(user), { replace: true });
    }
  }, [
    pending,
    user,
    userIsAdmin,
    isTournamentsLoaded,
    tournamentsById,
    location.pathname,
    navigate,
  ]);

  // The app gate opens on the cached profile, so the redirect above may have
  // routed by a role an admin has since changed. Redo it once the server copy
  // lands — otherwise a user promoted from NONE stays on an error page.
  useEffect(() => {
    if (!isSelfSynced || !user) return;
    if (redirectedForRole.current === null) return;
    if (redirectedForRole.current === user.role) return;

    redirectedForRole.current = user.role;
    navigate(getBaseRoute(user), { replace: true });
  }, [isSelfSynced, user, navigate]);

  // Switching the organization re-scopes every subscription, so the page the
  // user is on may belong to the one they just left. Send them to their root,
  // the same place login lands on.
  useEffect(() => {
    if (!user) {
      lastOrg.current = null;
      return;
    }

    const previous = lastOrg.current;
    lastOrg.current = activeOrg;
    // First resolve — login, or an invite granting the very first organization —
    // is not a switch: the login effect above already routed
    if (!previous || !activeOrg || previous === activeOrg) return;

    // The saved tournament belongs to the organization we just left
    clearSavedTournament();
    navigate(getBaseRoute(user), { replace: true });
  }, [user, activeOrg, navigate]);

  return null;
}
